import { Injectable } from '@angular/core';
import { ThesaurusPayload } from '@interfaces/thesaurus-payload.interface';
import { Thesaurus } from '@interfaces/thesaurus.interface';
import { ThesaurusSelectedTerm } from '@interfaces/thesaurus-selected-term.interface';
import { TermsService } from '@terms-service/terms.service';
import { ThesaurusService } from '@thesaurus-service/thesaurus.service';
import { ThesaurusPath } from '@interfaces/thesaurus-path.interface';

@Injectable({
  providedIn: 'root'
})
export class PayloadService {
  openParens: string = "(";
  closeParens: string = ")";
  exactQueryModifier: string = ".EXACT";
  explodeQueryModifier: string = ".EXPLODE";
  productTypes: { name: string, mjSubject: string, nonMjSubject: string }[] = [
    { name: 'MEDLINE', mjSubject: 'MJMESH', nonMjSubject: 'MESH' },
    { name: 'EMBASE', mjSubject: 'MJEMB', nonMjSubject: 'EMB' },
    { name: 'MLA', mjSubject: 'MLASU', nonMjSubject: 'MLASU' },
    { name: 'COMPANY', mjSubject: 'CORP', nonMjSubject: 'CORP' },
    { name: 'LEXICONTERM', mjSubject: '', nonMjSubject: 'FULLTEXT' },
    { name: 'OTHERS', mjSubject: 'MJMAINSUBJECT', nonMjSubject: 'MAINSUBJECT' },
    { name: 'LOOKUPCOMPANY', mjSubject: 'EXACT', nonMjSubject: 'EXACT' },
    { name: 'LOOKUPPQBUSCAT', mjSubject: 'EXACT', nonMjSubject: 'EXACT' },
    { name: 'PQBUSCAT', mjSubject: '', nonMjSubject: 'MAINSUBJECT' },
  ];

  constructor(
    public termsService: TermsService,
    public thesaurusService: ThesaurusService
  ) { }

  createThesaurusPayload(terms: ThesaurusSelectedTerm[], queryTermSeparator: string, targetFieldName: string, appendField: boolean, thesaurus: Thesaurus): ThesaurusPayload {
    const termsArray: string[] = terms.map(term => term.name);
    return { terms: termsArray, subjectQuery: this.createSubjectQuery(terms, queryTermSeparator, thesaurus, targetFieldName), targetFieldName: targetFieldName, appendField: appendField }
  }

  private createSubjectQuery(terms: ThesaurusSelectedTerm[], queryTermSeparator: string, thesaurus: Thesaurus, targetFieldName: string): string {
    let isLexicon = this.thesaurusService.isLexiconThesaurus(thesaurus.id ?? '');
    let isCorporation = this.thesaurusService.isCorporationThesaurus(thesaurus.id ?? '');
    let isBusiness = this.thesaurusService.isBusinessThesaurus(thesaurus.id ?? '');
    let isCorporationLookup = false;
    let isBusinessLookup = false;
    if(isCorporation) {
      isCorporationLookup = targetFieldName !== null && targetFieldName !== undefined && targetFieldName != '';
    }
    if(isBusiness) {
      isBusinessLookup = targetFieldName !== null && targetFieldName !== undefined && targetFieldName != '';
    }
    let finalTerms: string[] = [];

    if (isLexicon) {
      finalTerms = this.buildLexiconQueryString(terms, queryTermSeparator, thesaurus.id ?? '');
    } else if(isCorporation) {
      let thesaurusName = thesaurus.id ?? '';
      console.log("Thesaurus Name: ", thesaurusName);
      console.log("Target Field Name: ", targetFieldName);
      if (isCorporationLookup) {
        thesaurusName = "LOOKUP" + thesaurus.id;
        console.log("Modified Thesaurus Name for Corporation Lookup: ", thesaurusName);
        finalTerms = this.buildLexiconQueryString(terms, queryTermSeparator, thesaurusName);
      } else {
        finalTerms = this.buildQueryString(terms, thesaurus);
      }
    } else if(isBusiness) {
      let thesaurusName = thesaurus.id ?? '';
      console.log("Thesaurus Name: ", thesaurusName);
      console.log("Target Field Name: ", targetFieldName);
      if (isBusinessLookup) {
        thesaurusName = "LOOKUP" + thesaurus.id;
        console.log("Modified Thesaurus Name for Business Lookup: ", thesaurusName);
        finalTerms = this.buildLexiconQueryString(terms, queryTermSeparator, thesaurusName);
      } else {
        finalTerms = this.buildQueryString(terms, thesaurus);
      }
    }
    else {
      finalTerms = this.buildQueryString(terms, thesaurus);
    }

    let query = "";
    for (let i = 0; i < finalTerms.length; i++) {
      if (i > 0 && !isLexicon && !isCorporationLookup && !isBusinessLookup) {
        query += queryTermSeparator;
      }
      query += finalTerms[i];
    }

    return query;
  }


  // For all thesauri except Lexicon
  private buildQueryString (terms: ThesaurusSelectedTerm[], thesaurus: Thesaurus): string[] {
    let searchTerms: string[] = [];
    let noExactOnMj:boolean = thesaurus.noExactOnMajor || false;

    for (let entry of terms) {
      let exact: boolean = (thesaurus.useExact ?? false) && !(entry.majorChecked && noExactOnMj);
      let searchTerm: string = this.getMnemonic(entry, thesaurus.id);
      if (exact) {
        searchTerm = searchTerm + this.exactQueryModifier;
      }
      if (entry.explodeChecked) {
        searchTerm = searchTerm + this.explodeQueryModifier;
      }
      if(entry.paths && entry.explodeChecked && entry.notUnique) {
        let originalSearchTerm = searchTerm;
        for(let path of entry.paths) {
          let searchTerm = originalSearchTerm;
          searchTerm += this.openParens + "\"" + entry.name + this.getPathString(path) + "\"" + this.closeParens;
          searchTerms.push(searchTerm);        
        }
      } else if (entry.qualifiers.length > 0 ) {
        let originalTerm: string = searchTerm;
        let entryName: string = entry.name;

        for (let qualifier of entry.qualifiers) {
          let qualSearchTerm = originalTerm;
          qualSearchTerm += this.openParens + "\"" + entryName + " -- " + qualifier.name + "\"" + this.closeParens;
          searchTerms.push(qualSearchTerm);
        }
      } else {
        searchTerm += this.openParens + "\"" + entry.name + "\"" + this.closeParens;
        searchTerms.push(searchTerm);
      }
    }

    return searchTerms;
  }

  // For Lexicon thesaurus
  private buildLexiconQueryString(terms: ThesaurusSelectedTerm[], queryTermSeparator: string, thesaurusName: string) {
    let searchTerms:string[] = [];
    let product: any = this.getProduct(thesaurusName);
    console.log("Product for Lexicon Thesaurus: ", product);

    searchTerms.push(product.nonMjSubject);
    searchTerms.push(this.openParens);

    for (let i = 0; i < terms.length; i++) {
      let searchTerm: string = "";
      searchTerm += `\"${terms[i].name}\"`;

      if (i < (terms.length - 1)) {
        searchTerm += `${queryTermSeparator}`;
      }

      searchTerms.push(searchTerm);
    }

    searchTerms.push(this.closeParens);
    return searchTerms;
  }

  private getProduct(thesaurusName: string): any {
    if (thesaurusName.toLowerCase().indexOf("medline") !== -1) {
      return this.productTypes[0];
    }
    if (thesaurusName.toLowerCase().indexOf("embase") !== -1) {
      return this.productTypes[1];
    }
    if (thesaurusName.toLowerCase().indexOf("mla") !== -1) {
      return this.productTypes[2];
    }
    if (thesaurusName.toLowerCase().indexOf("lookupcompany") !== -1) {
      return this.productTypes[6];
    }
    if (thesaurusName.toLowerCase().indexOf("lookuppqbuscat") !== -1) {
      return this.productTypes[7];
    }
    if (thesaurusName.toLowerCase().indexOf("pqbuscat") !== -1) {
      return this.productTypes[8];
    }
    if (thesaurusName.toLowerCase().indexOf("company") !== -1) {
      return this.productTypes[3];
    }
    if (thesaurusName.toLowerCase().indexOf("acta_lexicon_qualifiers_en") !== -1) {
      return this.productTypes[4];
    }
    return this.productTypes[5];
  }

  private getMnemonic(term: ThesaurusSelectedTerm, thesaurusId: string): string {
    let product = this.getProduct(thesaurusId);
    if (term.majorChecked) {
      return product.mjSubject;
    }
    return product.nonMjSubject;
  }

  private getPathString(path: ThesaurusPath): string {
    return path.tn? ":" + path.tn : "";
  }

}